import { Controller } from "@hotwired/stimulus"
import { badgeFor, conflictFor, nextShiftDefaults, shiftError } from "../modules/store_hours.mjs"

// Horarios de tienda (Fase 2): turnos por día en el form de la tienda (wizard y staff). Agrega y
// quita turnos, maneja el checkbox "Abierto 24 horas" (una fila sin horas que apaga los turnos
// del día), pinta el badge que confirma un cierre al día siguiente y marca el tope de duración
// y los solapes antes de enviar, con el mismo cálculo que StoreHours#opening_hours_consistent
// (store_hours.mjs). El checkbox del día es solo UX: apagarlo marca _destroy en sus filas.
// Doc: _docs/features/stores/store_hours.md.
export default class extends Controller {
  static targets = ["day", "shifts", "list", "add", "shift", "allDay", "destroy", "badge", "conflict"]
  static values = {
    dayNames: Array,
    maxPerDay: { type: Number, default: 3 },
    maxShiftMinutes: { type: Number, default: 20 * 60 }
  }

  connect() {
    this.sequence = 0
    this.guard = this.guard.bind(this)
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.guard)
    this.refresh()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.guard)
  }

  toggleDay(event) {
    const day = this.dayOf(event.target)
    const open = event.target.checked
    this.shiftsBox(day).hidden = !open
    if (open) {
      this.setAllDay(day, this.allDayChecked(day))
    } else {
      this.shiftsIn(day).forEach(shift => this.setDestroyed(shift, true))
      this.setAllDayDestroyed(day, true)
    }
    this.refresh()
  }

  toggleAllDay(event) {
    this.setAllDay(this.dayOf(event.target), event.target.checked)
    this.refresh()
  }

  addShift(event) {
    this.insertShift(this.dayOf(event.target))
    this.refresh()
  }

  removeShift(event) {
    const shift = event.target.closest("[data-store-schedule-target~='shift']")
    const day = this.dayOf(shift)
    this.setDestroyed(shift, true)
    if (this.liveShiftsIn(day).length === 0) {
      day.querySelector(".schedule-day-check").checked = false
      this.shiftsBox(day).hidden = true
    }
    this.refresh()
  }

  // Form de staff (submit clásico): un error bloquea el envío. El wizard lo revisa en
  // steps#validateCurrentStep con la misma clase.
  guard(event) {
    this.refresh()
    const invalid = this.element.querySelector(".hours-shift.is-invalid, .hours-all-day.is-invalid")
    if (!invalid) return
    event.preventDefault()
    invalid.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // Badge y tope por turno, solapes entre todos los turnos vivos (incluidos los días de 24 horas);
  // "Agregar turno" se esconde al tope o con 24 horas.
  refresh() {
    // Se limpian TODAS las filas, también las apagadas u ocultas: un error viejo en una fila que ya
    // viaja con _destroy=1 dejaba el form bloqueado (guard y steps#validateCurrentStep lo veían).
    ;[...this.shiftTargets, ...this.allDayTargets].forEach(row => this.setConflict(row, null))

    const regular = this.liveShifts().map(shift => this.readShift(shift))
    const allDay = this.allDayTargets.filter(row => this.isLiveAllDay(row)).map(row => this.readAllDay(row))
    const rows = [...regular, ...allDay]

    regular.forEach(row => {
      const error = shiftError(row, this.maxShiftMinutesValue)
      this.setBadge(row.element, error ? "" : badgeFor(row, this.dayNamesValue))
      if (error) this.setConflict(row.element, error)
    })
    const valid = rows.filter(row => !row.element.classList.contains("is-invalid"))
    valid.forEach((a, index) => {
      valid.slice(index + 1).forEach(b => {
        const conflict = conflictFor(a, b, this.dayNamesValue)
        if (conflict) this.setConflict(conflict.shift.element, conflict.message)
      })
    })
    this.dayTargets.forEach(day => {
      this.addButtonOf(day).hidden = this.allDayChecked(day) || this.liveShiftsIn(day).length >= this.maxPerDayValue
    })
  }

  // 24 horas: viaja la fila sin horas y se apagan los turnos del día; al desmarcar vuelven o nace uno.
  setAllDay(day, on) {
    this.setAllDayDestroyed(day, !on)
    this.shiftsIn(day).forEach(shift => this.setDestroyed(shift, on))
    this.listOf(day).hidden = on
    if (!on && this.liveShiftsIn(day).length === 0) this.insertShift(day)
  }

  // El turno nuevo nace después del último del día (nextShiftDefaults): "Agregar turno" nunca
  // aparece en rojo por chocar con el turno que ya estaba.
  insertShift(day) {
    if (this.liveShiftsIn(day).length >= this.maxPerDayValue) return
    const existing = this.liveShiftsIn(day).map(shift => this.readShift(shift))
    // Índice numérico único: Rails descarta claves no numéricas en *_attributes.
    const html = day.querySelector("template").innerHTML.replace(/NEW_SHIFT/g, `${Date.now()}${this.sequence++}`)
    this.listOf(day).insertAdjacentHTML("beforeend", html)
    this.writeShift(this.listOf(day).lastElementChild, nextShiftDefaults(existing))
  }

  writeShift(element, { opens, closes }) {
    const set = (field, minute) => {
      element.querySelector(`select[name$='[${field}(4i)]']`).value = String(Math.floor(minute / 60)).padStart(2, "0")
      element.querySelector(`select[name$='[${field}(5i)]']`).value = String(minute % 60).padStart(2, "0")
    }
    set("opens_at", opens)
    set("closes_at", closes)
  }

  // Un turno quitado se oculta y viaja con _destroy=1; si aún no existe en la base, sale del DOM.
  setDestroyed(element, destroyed) {
    const persisted = element.querySelector("input[name$='[id]']")
    if (destroyed && !persisted) {
      element.remove()
      return
    }
    element.hidden = destroyed
    this.destroyFieldOf(element).value = destroyed ? "1" : "0"
  }

  setAllDayDestroyed(day, destroyed) {
    this.destroyFieldOf(this.allDayRowOf(day)).value = destroyed ? "1" : "0"
  }

  setBadge(element, text) {
    const badge = element.querySelector("[data-store-schedule-target~='badge']")
    badge.hidden = !text
    badge.textContent = text
  }

  setConflict(element, message) {
    const conflict = element.querySelector("[data-store-schedule-target~='conflict']")
    element.classList.toggle("is-invalid", Boolean(message))
    conflict.hidden = !message
    conflict.textContent = message || ""
  }

  // Lee weekday, apertura y cierre (minutos del día) de los selects de hora y minuto de la fila.
  readShift(element) {
    const minute = (field) =>
      Number(element.querySelector(`select[name$='[${field}(4i)]']`).value) * 60 +
      Number(element.querySelector(`select[name$='[${field}(5i)]']`).value)
    return { element, weekday: this.weekdayOf(element), opens: minute("opens_at"), closes: minute("closes_at"), allDay: false }
  }

  readAllDay(element) {
    return { element, weekday: this.weekdayOf(element), opens: 0, closes: 0, allDay: true }
  }

  weekdayOf(element) {
    return Number(element.querySelector("input[name$='[weekday]']").value)
  }

  liveShifts() {
    return this.shiftTargets.filter(shift => this.isLive(shift))
  }

  liveShiftsIn(day) {
    return this.shiftsIn(day).filter(shift => this.isLive(shift))
  }

  shiftsIn(day) {
    return Array.from(day.querySelectorAll("[data-store-schedule-target~='shift']"))
  }

  isLive(shift) {
    return !shift.hidden && this.destroyFieldOf(shift).value === "0"
  }

  isLiveAllDay(row) {
    return this.destroyFieldOf(row).value === "0"
  }

  allDayChecked(day) {
    return this.allDayRowOf(day).querySelector("input[type='checkbox']").checked
  }

  dayOf(element) {
    return element.closest("[data-store-schedule-target~='day']")
  }

  shiftsBox(day) {
    return day.querySelector("[data-store-schedule-target~='shifts']")
  }

  listOf(day) {
    return day.querySelector("[data-store-schedule-target~='list']")
  }

  addButtonOf(day) {
    return day.querySelector("[data-store-schedule-target~='add']")
  }

  allDayRowOf(day) {
    return day.querySelector("[data-store-schedule-target~='allDay']")
  }

  destroyFieldOf(element) {
    return element.querySelector("[data-store-schedule-target~='destroy']")
  }
}
